import { useEffect, useState } from 'react'
import { useTheme } from 'next-themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { NavLink } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { api } from '../lib/api'
import type { Conversation, Me } from '../lib/api'
import { useMessages } from '../lib/store'
import { NotificationBell } from './NotificationBell'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu'
import { cn } from '../lib/utils'

/** sortConversations puts pinned contacts first (most-recent pinned first),
 *  then everyone else by recency — the ordering every messenger uses for
 *  "keep this one near the top" without hiding the rest. */
function sortConversations(cs: Conversation[]): Conversation[] {
  return [...cs].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    return (b.last_message_at ?? 0) - (a.last_message_at ?? 0)
  })
}

/** relativeTime renders the timestamp column the way a messenger does. */
function relativeTime(ms?: number): string {
  if (!ms) return ''
  const diff = Date.now() - ms
  if (diff < 60_000) return 'now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d`
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

const PALETTES = [
  'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
  'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300',
  'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
]

/** ThemeToggle flips between light and dark, ignoring "system" as a separate
 *  click target once the user has expressed a preference — the same
 *  single-button convention as most messengers, rather than a three-way
 *  light/dark/system menu nobody asked for.
 *
 *  Guarded by `mounted`: `next-themes` cannot know the resolved theme until
 *  its effect runs client-side, and rendering a guess first would flip the
 *  icon a frame later — a visible flicker on every page load. */
function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  if (!mounted) return <Button size="icon-sm" variant="ghost" disabled aria-label="Toggle theme" />

  const dark = resolvedTheme === 'dark'
  return (
    <Button
      size="icon-sm"
      variant="ghost"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      data-testid="theme-toggle"
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {dark ? '☀️' : '🌙'}
    </Button>
  )
}

/** Initial is a cheap avatar: the first letter on a stable colour. */
function Initial({ name }: { name: string }) {
  // Hashing the name rather than the id keeps a contact the same colour after a
  // reinstall, which is what makes it a recognisable avatar rather than noise.
  let hash = 0
  for (const ch of name) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  const palette = PALETTES[hash % PALETTES.length]

  return (
    <div className={cn('flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-bold', palette)}>
      {(name.trim()[0] ?? '?').toUpperCase()}
    </div>
  )
}

export function ConversationList({
  me,
  onAddContact,
  onNewGroup,
  onSearch,
  onStarred,
}: {
  me?: Me
  onAddContact: () => void
  onNewGroup: () => void
  onSearch: () => void
  onStarred: () => void
}) {
  const queryClient = useQueryClient()
  const [showArchived, setShowArchived] = useState(false)

  // Refetched on every realtime conversation change via query invalidation in
  // the App shell; no polling, because the socket is the freshness mechanism.
  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: api.listConversations,
  })

  const toggle = useMutation({
    mutationFn: (vars: { id: string; patch: Partial<Conversation> }) => api.updateConversation(vars.id, vars.patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversations'] }),
  })

  const archivedCount = conversations.filter((c) => c.archived).length
  const visible = sortConversations(conversations.filter((c) => showArchived || !c.archived))

  const streaming = useMessages((s) => s.streaming)

  // Clerk's user object is the better source for the display name: a default
  // session token carries no profile claims, so /me only knows a name once the
  // webhook has synced one — and a deployment without a webhook would show
  // "signed in" forever. The server row stays the fallback.
  const { user } = useUser()
  const who =
    user?.fullName ||
    user?.primaryEmailAddress?.emailAddress ||
    me?.display_name ||
    me?.email ||
    'signed in'

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* gap shrinks and the two labelled buttons drop their text on narrow
          screens — five full-width controls plus a name would otherwise
          force this row to squeeze everything down to unreadable width. */}
      <div className="flex items-center gap-1 border-b px-2 py-3 md:gap-2 md:px-4">
        <div className="min-w-0 flex-1">
          <p className="font-bold">Parley</p>
          <p className="truncate text-xs text-muted-foreground">{who}</p>
        </div>
        <ThemeToggle />
        <NotificationBell />
        <Button size="icon-sm" variant="ghost" onClick={onSearch} data-testid="open-search" aria-label="Search">
          🔍
        </Button>
        <Button size="icon-sm" variant="ghost" onClick={onStarred} data-testid="open-starred" aria-label="Starred messages">
          ⭐
        </Button>
        <Button size="sm" variant="outline" onClick={onNewGroup} data-testid="new-group" className="px-2 md:px-3">
          <span className="hidden md:inline">New group</span>
          <span className="inline md:hidden">👥</span>
        </Button>
        <Button size="sm" onClick={onAddContact} data-testid="add-contact" className="px-2 md:px-3">
          <span className="hidden md:inline">Add agent</span>
          <span className="inline md:hidden">＋</span>
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="conversation-list">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="size-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="mb-1 font-medium">No agents yet</p>
            <p className="text-sm text-muted-foreground">
              Add one by URL — anything speaking the Mastra protocol works.
            </p>
          </div>
        ) : (
          visible.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted" data-testid="conversation-item">
              <NavLink to={`/c/${c.id}`} className="flex min-w-0 flex-1 gap-3">
                {({ isActive }) => (
                  <div className={cn('flex min-w-0 flex-1 items-center gap-3', isActive && 'bg-accent')}>
                    <Initial name={c.title || 'Agent'} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        {c.pinned && <span className="shrink-0 text-xs">📌</span>}
                        <p className="flex-1 truncate font-medium">{c.title || 'Untitled'}</p>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {relativeTime(c.last_message_at)}
                        </span>
                      </div>
                      <p className="truncate text-sm text-muted-foreground">
                        {streaming[c.id] ? 'typing…' : c.last_message_preview || 'No messages yet'}
                      </p>
                    </div>
                    {c.unread_count > 0 && !c.muted && (
                      <Badge className="rounded-full" data-testid="unread-badge">
                        {c.unread_count}
                      </Badge>
                    )}
                  </div>
                )}
              </NavLink>

              {/* Pin, mute, archive — the same three affordances every
                  messenger's long-press/context menu offers, folded into a
                  single dots menu rather than three always-visible buttons:
                  three icon buttons per row read as clutter, especially at
                  phone width where the row is already tight against the
                  NavLink's title and timestamp. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    className="shrink-0"
                    aria-label="Conversation options"
                    data-testid="conversation-menu-trigger"
                  >
                    ⋮
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    data-testid="pin-toggle"
                    onClick={() => toggle.mutate({ id: c.id, patch: { pinned: !c.pinned } })}
                  >
                    {c.pinned ? '📍 Unpin' : '📌 Pin'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="mute-toggle"
                    onClick={() => toggle.mutate({ id: c.id, patch: { muted: !c.muted } })}
                  >
                    {c.muted ? '🔔 Unmute' : '🔕 Mute'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="archive-toggle"
                    onClick={() => toggle.mutate({ id: c.id, patch: { archived: !c.archived } })}
                  >
                    {c.archived ? '📤 Unarchive' : '🗄 Archive'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))
        )}

        {archivedCount > 0 && (
          <div className="flex justify-center py-3">
            <Button size="xs" variant="ghost" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived">
              {showArchived ? 'Hide archived' : `Show ${archivedCount} archived`}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
